import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Search, Star, ArrowRight, Building, Award, Users, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { hotelInfo, stats, rooms, reviews, generateMockAvailability } from '../mockData';
import { format } from 'date-fns';
import { heroGallery } from '../mockData';
import Autoplay from "embla-carousel-autoplay";

const Home = () => {
  const [checkInDate, setCheckInDate] = useState();
  const [checkOutDate, setCheckOutDate] = useState();
  const [searchResults, setSearchResults] = useState(null);

  const handleSearch = () => {
    if (!checkInDate || !checkOutDate) {
      toast.error('Please select both check-in and check-out dates');
      return;
    }

    if (checkInDate >= checkOutDate) {
      toast.error('Check-out date must be after check-in date');
      return;
    }

    // Generate mock availability
    const results = generateMockAvailability(checkInDate, checkOutDate);
    setSearchResults(results);
    toast.success('Availability checked! See results below.');
    
    // Scroll to results
    setTimeout(() => {
      document.getElementById('search-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const handleBooking = (platform, roomName = '') => {
    const message = encodeURIComponent(
      `Hello! I'd like to book ${roomName} from ${checkInDate ? format(checkInDate, 'PP') : '[date]'} to ${checkOutDate ? format(checkOutDate, 'PP') : '[date]'}.`
    );

    if (platform === 'whatsapp') {
      window.open(`https://wa.me/${hotelInfo.whatsapp}?text=${message}`, '_blank');
    } else if (platform === 'agoda') {
      window.open(hotelInfo.bookingLinks.agoda, '_blank');
    } else if (platform === 'booking') {
      window.open(hotelInfo.bookingLinks.booking, '_blank');
    }
  };

  const allReviews = [...reviews.google, ...reviews.agoda, ...reviews.booking];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          {/* <div className="inline-block mb-6 px-6 py-2 bg-cyan-500/20 backdrop-blur-md rounded-full border border-cyan-400/30 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <span className="text-cyan-300 font-medium">✨ Luxury Boutique Hotel in Kandy</span>
          </div> */}
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-150">
            {hotelInfo.name}
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-4 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300">
            {hotelInfo.tagline}
          </p>
          
          <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-10 duration-700 delay-450">
            {hotelInfo.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in fade-in slide-in-from-bottom-12 duration-700 delay-600">
            <Button
              onClick={() => document.getElementById('check-availability')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-500 hover:to-cyan-600 text-white text-lg px-8 py-6 shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105"
            >
              <CalendarIcon className="mr-2" size={20} />
              Check Availability
            </Button>
            <Link to="/about">
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-md border-white/30 text-white hover:bg-white/20 text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
              >
                Discover More
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon === 'building' ? Building : stat.icon === 'award' ? Award : stat.icon === 'users' ? Users : Star;
              return (
                <Card
                  key={index}
                  className="text-center p-6 bg-white hover:shadow-xl transition-all duration-300 hover:scale-105 border-none shadow-md"
                >
                  <CardContent className="p-0">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-cyan-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</p>
                    <p className="text-gray-600">{stat.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Carousel Section */}
<section className="py-20 bg-white">
  <div className="container mx-auto px-4">
    <div className="text-center mb-10">
      <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
        Experience Our Spaces
      </h2>
      <p className="text-gray-600 text-lg">
        A glimpse into comfort, elegance, and luxury
      </p>
    </div>

    <Carousel
      className="max-w-6xl mx-auto"
      opts={{ loop: true }}
      plugins={[
        Autoplay({
          delay: 3500,
          stopOnInteraction: false
        })
      ]}
    >
      <CarouselContent>
        {heroGallery.map((img, index) => (
          <CarouselItem key={index}>
            <div className="overflow-hidden rounded-2xl shadow-2xl">
              <img
                src={img}
                alt={`Hotel view ${index + 1}`}
                className="w-full h-[500px] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>

      <CarouselPrevious />
      <CarouselNext />
    </Carousel>
  </div>
</section>

      {/* Check Availability Section */}
      <section id="check-availability" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block mb-4 px-6 py-2 bg-cyan-100 rounded-full">
              <span className="text-cyan-600 font-semibold">Book Your Stay</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Check Availability</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Find your perfect room and create unforgettable memories
            </p>
          </div>

          <Card className="max-w-4xl mx-auto shadow-2xl border-none">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Check-in Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Check-in Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal h-12 border-2 hover:border-cyan-400 transition-colors"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-cyan-500" />
                        {checkInDate ? format(checkInDate, 'PP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkInDate}
                        onSelect={setCheckInDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Check-out Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Check-out Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal h-12 border-2 hover:border-cyan-400 transition-colors"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-cyan-500" />
                        {checkOutDate ? format(checkOutDate, 'PP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkOutDate}
                        onSelect={setCheckOutDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Search Button */}
                <div className="flex items-end">
                  <Button
                    onClick={handleSearch}
                    className="w-full h-12 bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-500 hover:to-cyan-600 text-white font-semibold shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105"
                  >
                    <Search className="mr-2" size={18} />
                    Search Rooms
                  </Button>
                </div>
              </div>

              {/* Booking Platforms */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-center text-gray-600 mb-4 font-medium">Or book instantly through:</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => handleBooking('agoda')}
                    variant="outline"
                    className="border-2 border-orange-400 text-orange-600 hover:bg-orange-50 hover:border-orange-500 transition-all duration-300 hover:scale-105"
                  >
                    Agoda
                  </Button>
                  <Button
                    onClick={() => handleBooking('booking')}
                    variant="outline"
                    className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600 transition-all duration-300 hover:scale-105"
                  >
                    Booking.com
                  </Button>
                  <Button
                    onClick={() => handleBooking('whatsapp')}
                    variant="outline"
                    className="border-2 border-green-500 text-green-600 hover:bg-green-50 hover:border-green-600 transition-all duration-300 hover:scale-105"
                  >
                    WhatsApp
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Search Results */}
      {searchResults && (
        <section id="search-results" className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Available Rooms</h2>
            <p className="text-center text-gray-600 mb-12">
              Showing availability from {checkInDate && format(checkInDate, 'PP')} to {checkOutDate && format(checkOutDate, 'PP')}
            </p>
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {searchResults.map((room) => (
                <Card key={room.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-none shadow-lg">
                  <img
                    src={room.image}
                    alt={room.name}
                    className="w-full h-64 object-cover"
                  />
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-2xl font-bold text-gray-900">{room.name}</h3>
                      {room.available ? (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          {room.remainingRooms} Available
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                          Sold Out
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-4">{room.description}</p>
                    <div className="flex justify-between items-center">
                      <p className="text-3xl font-bold text-cyan-600">{room.price}<span className="text-base text-gray-500">/night</span></p>
                      {room.available && (
                        <Button
                          onClick={() => handleBooking('whatsapp', room.name)}
                          className="bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-500 hover:to-cyan-600 text-white"
                        >
                          Book Now
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

{/* Guest Reviews Section */}
<section className="py-20 bg-white">
  <div className="container mx-auto px-4">
    <div className="text-center mb-12">
      <div className="inline-block mb-4 px-6 py-2 bg-cyan-100 rounded-full">
        <span className="text-cyan-600 font-semibold">Guest Reviews</span>
      </div>
      <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Guests Say</h2>
      <p className="text-gray-600 text-lg max-w-2xl mx-auto">
        {/* TODO: Replace with actual reviews from Google Reviews API, Agoda API, and Booking.com API */}
        Read authentic reviews from our valued guests across multiple platforms
      </p>
    </div>

    <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
      {allReviews.slice(0, 6).map((review) => (
        <Card key={review.id} className="hover:shadow-xl transition-all duration-300 hover:scale-105 border-none shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={18}
                  className={i < Math.floor(review.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                />
              ))}
              <span className="text-sm text-gray-500 ml-2">{review.date}</span>
            </div>
            <p className="text-gray-700 mb-4 leading-relaxed">"{review.text}"</p>
            <div className="flex justify-between items-center">
              <p className="font-semibold text-gray-900">{review.author}</p>
              <span className="px-3 py-1 bg-gradient-to-r from-cyan-100 to-cyan-50 text-cyan-700 rounded-full text-xs font-semibold">
                {review.platform}
              </span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
</section>

{/* Featured Rooms Preview */}
<section className="py-20 bg-gradient-to-b from-gray-50 to-white">
  <div className="container mx-auto px-4">
    <div className="text-center mb-12">
      <div className="inline-block mb-4 px-6 py-2 bg-cyan-100 rounded-full">
        <span className="text-cyan-600 font-semibold">Our Rooms</span>
      </div>
      <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Luxurious Accommodation</h2>
      <p className="text-gray-600 text-lg max-w-2xl mx-auto">
        Choose from our carefully designed rooms and suites
      </p>
    </div>

    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
      {rooms.map((room, index) => (
        <Card
          key={room.id}
          className="group overflow-hidden hover:shadow-2xl transition-all duration-500 hover:scale-105 border-none shadow-lg"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <div className="relative overflow-hidden">
            <img
              src={room.image}
              alt={room.name}
              className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute top-4 right-4 px-4 py-2 bg-cyan-500 text-white font-bold rounded-full shadow-lg">
              {room.price}/night
            </div>
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{room.name}</h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{room.description}</p>
            <Link to="/accommodation">
              <Button
                variant="outline"
                className="w-full border-2 border-cyan-400 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-500 transition-all"
              >
                View Details
              </Button>
            </Link>
          </CardContent>
        </Card>
      ))}
    </div>

    <div className="text-center mt-12">
      <Link to="/accommodation">
        <Button
          size="lg"
          className="bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-500 hover:to-cyan-600 text-white px-8 shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105"
        >
          View All Rooms
          <ArrowRight className="ml-2" size={20} />
        </Button>
      </Link>
    </div>
  </div>
</section>
</div>
);
};

export default Home;
